import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  FileText, 
  Search, 
  Download, 
  Edit, 
  Trash2, 
  Calendar,
  Users,
  Clock,
  Filter,
  Eye,
  Database
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const SheetsPage = () => {
  const { api, canEdit } = useAuth();
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadSheets();
  }, []);

  const loadSheets = async () => {
    try {
      const response = await api.get('/function-sheets');
      setSheets(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Function Sheets');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await api.delete(`/function-sheets/${deleteId}`);
      setSheets(sheets.filter(s => s.id !== deleteId));
      toast.success('Function Sheet gelöscht');
    } catch (error) {
      toast.error('Löschen fehlgeschlagen');
    } finally {
      setDeleteId(null);
    }
  };

  const handleDownload = async (sheet) => {
    try {
      const response = await api.get(`/function-sheets/${sheet.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FunctionSheet_${sheet.va_facts.title || 'Veranstaltung'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF heruntergeladen!');
    } catch (error) {
      toast.error('Download fehlgeschlagen');
    }
  };

  const filteredSheets = sheets.filter(sheet => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      sheet.va_facts.title?.toLowerCase().includes(searchLower) ||
      sheet.va_facts.customer?.toLowerCase().includes(searchLower) ||
      sheet.va_facts.event_date?.includes(searchTerm) ||
      sheet.va_facts.gastronovi_customer_number?.toLowerCase().includes(searchLower);
    
    const matchesStatus = statusFilter === 'all' || sheet.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-burgundy">Wird geladen...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sheets-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-burgundy">Function Sheets</h1>
          <p className="text-charcoal-500 mt-1">
            {sheets.length} {sheets.length === 1 ? 'Veranstaltung' : 'Veranstaltungen'}
          </p>
        </div>
        
        {canEdit() && (
          <Link to="/upload">
            <Button 
              className="bg-burgundy hover:bg-burgundy-800 text-white font-serif"
              data-testid="new-sheet-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              Neues Sheet
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-400" />
              <Input
                placeholder="Suche nach Kunde, Titel, Datum oder Kundennummer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-stone-300"
                data-testid="search-input"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 border-stone-300" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="published">Veröffentlicht</SelectItem>
                <SelectItem value="draft">Entwurf</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Sheets Table */}
      {filteredSheets.length > 0 ? (
        <Card className="shadow-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="font-serif text-burgundy">Veranstaltung</TableHead>
                <TableHead className="font-serif text-burgundy">Datum</TableHead>
                <TableHead className="font-serif text-burgundy">Gäste</TableHead>
                <TableHead className="font-serif text-burgundy">Status</TableHead>
                <TableHead className="font-serif text-burgundy">Erstellt von</TableHead>
                <TableHead className="font-serif text-burgundy text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSheets.map((sheet) => (
                <TableRow 
                  key={sheet.id} 
                  className="hover:bg-stone-50 transition-colors"
                  data-testid={`sheet-row-${sheet.id}`}
                >
                  <TableCell>
                    <div>
                      <p className="font-semibold text-charcoal-800">
                        {sheet.va_facts.title || 'Unbenannt'}
                      </p>
                      <p className="text-sm text-charcoal-500">
                        {sheet.va_facts.customer}
                      </p>
                      {sheet.va_facts.gastronovi_customer === 'Ja' && sheet.va_facts.gastronovi_customer_number && (
                        <p className="text-xs text-gold-600 flex items-center gap-1 mt-1">
                          <Database className="h-3 w-3" />
                          Nr. {sheet.va_facts.gastronovi_customer_number}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-charcoal-600">
                      <Calendar className="h-4 w-4" />
                      {sheet.va_facts.event_date || '-'}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-charcoal-400">
                      <Clock className="h-3 w-3" />
                      {sheet.va_facts.event_time || '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-charcoal-600">
                      <Users className="h-4 w-4" />
                      {sheet.va_facts.guest_count || '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      className={sheet.status === 'published' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-gold-100 text-gold-700'
                      }
                    >
                      {sheet.status === 'published' ? 'Veröffentlicht' : 'Entwurf'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <p className="text-charcoal-600">{sheet.created_by_name}</p>
                    <p className="text-xs text-charcoal-400">
                      {new Date(sheet.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/sheets/${sheet.id}`}>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-charcoal-600 hover:text-burgundy"
                          data-testid={`view-${sheet.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                      
                      {canEdit() && (
                        <Link to={`/editor/${sheet.id}`}>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-charcoal-600 hover:text-burgundy"
                            data-testid={`edit-${sheet.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                      )}
                      
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDownload(sheet)}
                        className="text-charcoal-600 hover:text-burgundy"
                        data-testid={`download-${sheet.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      
                      {canEdit() && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => setDeleteId(sheet.id)}
                          className="text-charcoal-600 hover:text-red-600"
                          data-testid={`delete-${sheet.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="shadow-card">
          <CardContent className="py-12 text-center">
            <FileText className="h-16 w-16 mx-auto text-stone-300 mb-4" />
            <h3 className="font-serif text-xl text-charcoal-700 mb-2">
              {searchTerm || statusFilter !== 'all' 
                ? 'Keine Ergebnisse gefunden' 
                : 'Noch keine Function Sheets'
              }
            </h3>
            <p className="text-charcoal-500 mb-4">
              {searchTerm || statusFilter !== 'all'
                ? 'Versuchen Sie eine andere Suche'
                : 'Laden Sie eine Auftragsbestätigung hoch, um zu beginnen'
              }
            </p>
            {canEdit() && !searchTerm && statusFilter === 'all' && (
              <Link to="/upload">
                <Button className="bg-burgundy hover:bg-burgundy-800 text-white">
                  Erste Auftragsbestätigung hochladen
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-burgundy">
              Function Sheet löschen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Das Function Sheet wird dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-stone-300">Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SheetsPage;
